import { Star, MapPin, Phone, ShieldCheck, Home } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";

const BENTO_IMG =
    "https://images.unsplash.com/photo-1691571026798-1c5819901996?crop=entropy&cs=srgb&fm=jpg&q=85";

export const Bento = () => (
    <section id="why-us" data-testid="why-us-bento-section" className="py-24 sm:py-32 bg-[#13191E] border-y border-white/10 corrugation">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <FadeUp className="mb-14">
                <Eyebrow>Why One God Roofing</Eyebrow>
                <h2 className="font-display font-bold uppercase text-4xl sm:text-6xl leading-[1.02] mt-5">
                    Built on trust,<br />
                    <span className="text-outline">roofed for life.</span>
                </h2>
            </FadeUp>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FadeUp className="md:col-span-2 md:row-span-2" y={40}>
                    <article
                        data-testid="bento-card-materials"
                        className="relative h-full min-h-[380px] md:min-h-[520px] overflow-hidden border border-white/10 group"
                    >
                        <img
                            src={BENTO_IMG}
                            alt="Roofer laying sheets on a wide expanse of roof"
                            className="absolute inset-0 w-full h-full object-cover img-treated transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F12] via-[#0B0F12]/45 to-transparent" />
                        <div className="relative h-full flex flex-col justify-end p-7 sm:p-10">
                            <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-4">
                                <ShieldCheck size={14} />
                                Our promise
                            </span>
                            <h3 className="font-display font-bold uppercase text-3xl sm:text-4xl leading-tight">
                                Quality materials for your building.
                            </h3>
                            <p className="mt-4 max-w-md text-sm sm:text-base text-[#94A3B8] leading-relaxed">
                                Heavy-gauge metal sheets, corrosion-resistant coatings and
                                tight, leak-proof joins — fitted by hands that treat every
                                roof like their own.
                            </p>
                        </div>
                    </article>
                </FadeUp>

                <FadeUp delay={0.1}>
                    <article
                        data-testid="bento-card-reputation"
                        className="h-full border border-white/10 bg-[#0B0F12] p-7 flex flex-col justify-between gap-6 hover:border-amber-500/40 transition-colors"
                    >
                        <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-500">
                            <Star size={14} />
                            Google track record
                        </span>
                        <div>
                            <p className="font-display font-extrabold text-6xl sm:text-7xl text-[#F8FAFC] leading-none">
                                4.4
                            </p>
                            <div className="flex gap-1 text-amber-500 mt-3">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} size={15} fill={i < 4 ? "currentColor" : "none"} strokeWidth={1.5} className={i === 4 ? "opacity-40" : ""} />
                                ))}
                            </div>
                            <p className="mt-3 text-sm text-[#94A3B8]">Rated across 7 Google reviews from customers in Sunyani.</p>
                        </div>
                    </article>
                </FadeUp>

                <FadeUp delay={0.2}>
                    <article
                        data-testid="bento-card-sunyani-local"
                        className="h-full border border-white/10 bg-[#0B0F12] p-7 flex flex-col justify-between gap-6 hover:border-amber-500/40 transition-colors"
                    >
                        <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-500">
                            <Home size={14} />
                            Local authority
                        </span>
                        <div>
                            <MapPin size={26} className="text-amber-500 mb-4" />
                            <h3 className="font-display font-bold uppercase text-xl">Proudly Sunyani</h3>
                            <p className="mt-2 text-sm text-[#94A3B8] leading-relaxed">
                                Based on J.H. Owusu Acheampong St — serving Sunyani and
                                nearby areas with fast on-site visits.
                            </p>
                        </div>
                    </article>
                </FadeUp>

                <FadeUp delay={0.15} className="md:col-span-3">
                    <article
                        data-testid="bento-card-emergency"
                        className="h-full border border-white/10 bg-[#0B0F12] p-7 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:border-amber-500/40 transition-colors"
                    >
                        <div>
                            <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-3">
                                <Phone size={14} />
                                Straight through on the phone
                            </span>
                            <h3 className="font-display font-bold uppercase text-2xl">
                                Storm damage? Talk to a roofer, not a machine.
                            </h3>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <a href="tel:+233553237357" data-testid="bento-phone-0553237357" className="bg-amber-500 hover:bg-amber-400 text-[#0F172A] font-semibold text-sm px-6 py-3.5 transition-colors">
                                055 323 7357
                            </a>
                            <a href="tel:+233279912807" data-testid="bento-phone-0279912807" className="border border-white/20 hover:border-amber-500 hover:text-amber-500 text-white text-sm px-6 py-3.5 transition-colors">
                                027 991 2807
                            </a>
                        </div>
                    </article>
                </FadeUp>
            </div>
        </div>
    </section>
);

export default Bento;
